import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button, Snackbar } from '@mui/material';

import { useAuth } from '@clerk/clerk-react';
import { AdminApprovalProvider } from '../components/auth/AdminApprovalProvider';
import { PlannerResultsPage } from './PlannerResultsPage';
import { restoreSearch } from '../services/searchHistoryApi';
import { usePlanner } from '../contexts/PlannerContext';
import { createLogger } from '../utils/logger';
import { surfaceBackgroundSx } from '../theme/backgrounds';

/**
 * SharedTripPage - Handles shared trip links that only use the search ID.
 * When a user visits /trip/:searchId, this component:
 * 1. Fetches the search data (unless already hydrated)
 * 2. Restores the full trip state in the planner context
 * 3. Renders the standard PlannerResultsPage without changing the URL
 */
export const SharedTripPage: React.FC = () => {
  const { searchId } = useParams<{ searchId: string }>();
  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const {
    hydrateFromTravelData,
    currentSearchId,
    travelData,
    clearCurrentPlan,
    activeChatTripId,
    setShareViewLock,
  } = usePlanner();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNoTripNotice, setShowNoTripNotice] = useState(false);

  // Track which search ID we've loaded to prevent re-fetching during updates
  const loadedSearchIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!searchId) {
      setShareViewLock(null);
      return;
    }

    const shouldLock = !activeChatTripId || activeChatTripId !== searchId;
    setShareViewLock(shouldLock ? searchId : null);

    return () => {
      setShareViewLock(null);
    };
  }, [searchId, activeChatTripId, setShareViewLock]);

  useEffect(() => {
    const loadSharedTrip = async () => {
      if (!searchId) {
        setError('No trip ID provided');
        setLoading(false);
        return;
      }

      // If we've already loaded this search ID, don't reload it
      if (loadedSearchIdRef.current === searchId) {
        setError(null);
        setLoading(false);
        return;
      }

      // If the currentSearchId matches and we have data, we're already showing this trip
      if (currentSearchId === searchId && travelData) {
        loadedSearchIdRef.current = searchId;
        setError(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await restoreSearch(searchId);

        if (result.travel_state) {
          const restoredSearchId = result.search_history?.search_id ?? searchId;
          const citiesCount = result.travel_state?.cities?.length ?? 0;
          const isMultiCity = result.travel_state?.plan_mode === 'multi_city' ||
                              Boolean(result.multi_city_plan) ||
                              citiesCount > 1 ||
                              Boolean(result.country_destination_info || result.travel_state?.country_destination_info);

          // Mark as loaded BEFORE hydrating to prevent infinite loop
          loadedSearchIdRef.current = searchId;

          // 🔍 [RESTORE] Debug: Log what we received from restoreSearch
          console.log('📦 [RESTORE] Received restoreSearch result (SharedTripPage):', {
            searchId,
            isMultiCity,
            hasTopLevelCountryInfo: !!result.country_destination_info,
            hasTopLevelDestinationInfo: !!result.destination_info,
            hasTravelStateCountryInfo: !!result.travel_state.country_destination_info,
            hasTravelStateDestinationInfo: !!result.travel_state.destination_info,
            planMode: result.travel_state?.plan_mode,
            citiesCount: result.travel_state?.cities?.length || 0,
          });

          // Prefer country info for multi-city; check both top-level and nested in travel_state
          // Check top-level first, then fall back to travel_state.country_destination_info
          const countryInfo = isMultiCity
            ? (result.country_destination_info || result.travel_state.country_destination_info || null)
            : null;
          const cityInfo = !isMultiCity
            ? (result.destination_info || result.travel_state.destination_info || null)
            : (countryInfo ? null : (result.destination_info || result.travel_state.destination_info || null));

          console.log('✅ [RESTORE] Extracted destination info (SharedTripPage):', {
            countryInfo: countryInfo ? { name: countryInfo.name, hasHistory: !!countryInfo.history } : null,
            cityInfo: cityInfo ? { name: cityInfo.name } : null,
            isMultiCity,
          });

          // Hydrate with destination info included in options, preferring country info for multi-city
          hydrateFromTravelData(result.travel_state, {
            merge: false,
            searchId: restoredSearchId,
            destinationInfo: cityInfo,
            countryDestinationInfo: countryInfo,
          });

          setLoading(false);
        } else {
          setError('Trip data is incomplete');
          setLoading(false);
        }
      } catch (err: any) {
        log.error('Failed to load shared trip', err);
        setError(err.message || 'Failed to load this trip. It may have been deleted or the link is invalid.');
        setLoading(false);
      }
    };

    loadSharedTrip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchId]); // Only re-run when searchId changes, not when travelData updates

  useEffect(() => {
    // Don't redirect while we're still loading the requested trip
    // This prevents race conditions where cached state might briefly mismatch the URL
    if (loading) {
      return;
    }

    if (!currentSearchId || currentSearchId === searchId) {
      return;
    }

    navigate(`/trip/${currentSearchId}`, { replace: true });
  }, [currentSearchId, searchId, navigate, loading]);

  useEffect(() => {
    if (!error) {
      return;
    }

    setShowNoTripNotice(true);
    (async () => {
      try {
        await clearCurrentPlan();
      } catch (err) {
        log.error('Failed to clear stale current plan after error', err);
      }
    })();
  }, [error, clearCurrentPlan]);

  const handleNoticeClose = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowNoTripNotice(false);
  };

  const noTripSnackbar = (
    <Snackbar
      open={showNoTripNotice}
      autoHideDuration={6000}
      onClose={handleNoticeClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleNoticeClose} severity="info" sx={{ width: '100%' }}>
        No current trips are generated yet. Create a new plan to see it here.
      </Alert>
    </Snackbar>
  );

  if (loading) {
    return (
      <Box
        sx={(theme) => ({
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...surfaceBackgroundSx(theme),
        })}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: 'primary.main', mb: 2 }} size={48} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Loading shared trip...
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled', mt: 1 }}>
            Just a moment while we prepare your itinerary
          </Typography>
        </Box>
        {noTripSnackbar}
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={(theme) => ({
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          ...surfaceBackgroundSx(theme),
        })}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            No current trips are available for this link. The plan may have been removed or never generated.
          </Alert>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Unable to load this trip
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            The trip you're looking for might have been deleted or the link may be invalid.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => navigate('/')}>
              Create a New Trip
            </Button>
            <Button variant="outlined" onClick={() => navigate('/history')}>
              View My Trips
            </Button>
          </Box>
        </Box>
        {noTripSnackbar}
      </Box>
    );
  }

  const plannerContent = (
    <Box sx={{ pb: 12 }}>
      <PlannerResultsPage disableAutoRestore />
    </Box>
  );

  if (isSignedIn) {
    return (
      <>
        <AdminApprovalProvider>{plannerContent}</AdminApprovalProvider>
        {noTripSnackbar}
      </>
    );
  }

  return (
    <>
      {plannerContent}
      {noTripSnackbar}
    </>
  );
};
const log = createLogger('SharedTripPage');
